import React from 'react';
import RoleCard from '../../components/admin/RoleCard';

/**
 * SystemRolesPage - Display all available role types with permission descriptions
 *
 * Feature: System Administration Interface (001-admin-settings)
 * User Story: US1 - View System Role Definitions (Priority P1 - MVP)
 *
 * Purpose: Help admins understand access levels before assigning roles to users
 *
 * Multi-tenancy (v0.13.0) changed the model this page documents. There is no longer a single
 * installation-wide ladder. Authority now comes from two independent tiers, and describing
 * them accurately matters because this is the page people consult before granting access:
 *
 * - Platform Administrator — Akhile's back office. Not a workspace role at all.
 * - Workspace roles (Owner / Admin / Portfolio Manager / Member / Viewer) — authority inside
 *   ONE workspace. A Portfolio Manager of one client has no standing with another.
 *
 * Sections:
 * - Platform role: the Akhile back office
 * - Workspace roles: authority within a single workspace
 * - Project & Programme access roles: Editor, Viewer (granular, within a workspace)
 */
const SystemRolesPage: React.FC = () => {
    // System role definitions
    const systemRoles = [
        {
            title: 'Platform Administrator',
            description: 'Akhile staff only. Runs the platform itself: creates client workspaces, sets their purchased seats, and configures installation-wide settings. This is NOT a workspace role — it grants no access inside any client workspace without a membership there.',
            icon: 'admin' as const,
            permissions: [
                'Create client workspaces and set their purchased seat count',
                'Suspend or reactivate a workspace',
                'Add a workspace\u2019s first Owner, and join a client workspace without consuming one of their paid seats',
                'Configure email, authentication policy and installation settings',
                'Access the audit log across all workspaces',
                'Approve or reject account registrations',
                'Cannot see a client\u2019s projects, tasks or people without a membership in that workspace',
                'Never granted to a client, however senior they are in their own workspace'
            ]
        },
        {
            title: 'Owner',
            description: 'The client\u2019s top role in their own workspace. Runs the team: invites people, sets their roles, and manages seat usage.',
            icon: 'admin' as const,
            permissions: [
                'Invite people to this workspace and remove them',
                'Set anyone\u2019s workspace role, including another Owner',
                'See seats used versus purchased (invitations already hold a seat)',
                'Everything a Portfolio Manager can do across this workspace',
                'Cannot invite past the purchased seat limit \u2014 contact Akhile for more',
                'Cannot reach the platform back office (audit log, email, authentication policy)',
                'Cannot see any other workspace'
            ]
        },
        {
            title: 'Admin',
            description: 'Workspace administrator. Same team-management powers as Owner, for delegating day-to-day administration.',
            icon: 'admin' as const,
            permissions: [
                'Invite people to this workspace, set roles, remove members',
                'Everything a Portfolio Manager can do across this workspace',
                'Cannot reach the platform back office',
                'Cannot see any other workspace'
            ]
        },
        {
            title: 'Portfolio Manager',
            description: 'Portfolio-wide authority inside ONE workspace, without team-management powers. For people who run programmes and projects across their organisation.',
            icon: 'admin' as const,
            permissions: [
                'Create, edit, and delete any programme in this workspace',
                'Edit any project, task, and milestone in this workspace',
                'Assign and manage programme/project Editor & Viewer access roles',
                'View all of this workspace\u2019s projects and data',
                'Cannot invite or remove people, or change seat usage',
                'Cannot configure settings, email, or authentication policy',
                'Cannot access the audit log',
                'Has no authority in any other workspace'
            ]
        },
        {
            title: 'Member',
            description: 'Standard user with edit access inside their workspace. Can create and manage projects, tasks, and collaborate with teams.',
            icon: 'member' as const,
            permissions: [
                'Create and manage own projects',
                'Create, edit, and delete tasks in accessible projects',
                'View all projects in their own workspace (default access)',
                'Edit projects where assigned as Editor',
                'Comment on tasks and participate in discussions',
                'Update own user profile and preferences',
                'Cannot manage other users or system settings',
                'Cannot assign roles or change permissions'
            ]
        },
        {
            title: 'Viewer',
            description: 'Read-only access within their workspace. Can view projects and tasks but cannot make changes.',
            icon: 'viewer' as const,
            permissions: [
                'View all projects and tasks in their own workspace (read-only)',
                'View user profiles and team members',
                'View reports and dashboards',
                'Export data for personal use',
                'Update own user profile only',
                'Cannot create, edit, or delete any resources',
                'Cannot comment or participate in discussions',
                'Useful for stakeholders needing visibility without edit rights'
            ]
        }
    ];

    // Project and programme role definitions
    const projectProgrammeRoles = [
        {
            title: 'Editor',
            description: 'Full edit access to specific project or programme. Can manage tasks, milestones, and team members.',
            icon: 'editor' as const,
            variant: 'project' as const,
            permissions: [
                'Create, edit, and delete tasks within assigned project/programme',
                'Manage project/programme milestones and deadlines',
                'Assign tasks to team members',
                'Update project/programme details and description',
                'Add comments and attachments',
                'View all project/programme data and reports',
                'Applies to specific project or all projects in programme',
                'Overrides system Member role restrictions for assigned scope'
            ]
        },
        {
            title: 'Viewer',
            description: 'Read-only access to specific project or programme. Can view all details but cannot make changes.',
            icon: 'viewer' as const,
            variant: 'project' as const,
            permissions: [
                'View all tasks and project/programme data',
                'View project/programme milestones and reports',
                'View team members and assignments',
                'Export project/programme data',
                'Cannot create, edit, or delete any resources',
                'Cannot comment or participate in discussions',
                'Applies to specific project or all projects in programme',
                'Useful for stakeholders needing project visibility'
            ]
        }
    ];

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
            {/* Page header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    System Roles
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Understand role definitions and permission levels before assigning roles to users. 
                    The system implements a "highest permission wins" algorithm when multiple roles apply.
                </p>
            </div>

            {/* System Roles Section */}
            <section className="mb-12">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Platform &amp; Workspace Roles
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Platform Administrator runs the installation itself and is held only by
                        Akhile staff. The rest are <strong>workspace</strong> roles: they apply
                        inside one workspace and carry no authority in any other, so the same
                        person can be an Owner of one client and a Member of another.
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {systemRoles.map((role) => (
                        <RoleCard
                            key={role.title}
                            title={role.title}
                            description={role.description}
                            permissions={role.permissions}
                            icon={role.icon}
                            variant="system"
                        />
                    ))}
                </div>
            </section>

            {/* Project & Programme Roles Section */}
            <section>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        Project & Programme Roles
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Granular roles that apply to specific projects or programmes. 
                        Users can have multiple project/programme roles. These roles can elevate permissions 
                        beyond the user's system role for specific scopes.
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {projectProgrammeRoles.map((role) => (
                        <RoleCard
                            key={role.title}
                            title={role.title}
                            description={role.description}
                            permissions={role.permissions}
                            icon={role.icon}
                            variant={role.variant}
                        />
                    ))}
                </div>
            </section>

            {/* Permission Resolution Info */}
            <div className="mt-12 p-6 bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-200 dark:border-primary-800 rounded-lg">
                <h3 className="text-lg font-bold text-primary-900 dark:text-primary-100 mb-3">
                    Permission Resolution: "Highest Permission Wins"
                </h3>
                <div className="space-y-2 text-sm text-primary-800 dark:text-primary-200">
                    <p className="font-semibold">When a user has multiple roles, permissions are resolved in this order:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                        <li><strong>System Admin / Portfolio Manager</strong> → Full access to all resources (bypasses all other checks)</li>
                        <li><strong>Programme Editor</strong> → Edit access to all projects within the programme</li>
                        <li><strong>Project Editor</strong> → Edit access to specific project</li>
                        <li><strong>System Member</strong> → Default edit access where no specific role assigned</li>
                        <li><strong>Viewer roles</strong> → Read-only access at any level</li>
                    </ol>
                    <p className="mt-3 pt-3 border-t border-primary-300 dark:border-primary-700">
                        <strong>Example:</strong> A user with System Member role + Project Editor role on "Website Redesign" 
                        can edit that specific project but only has standard member access to other projects.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SystemRolesPage;
